<template>
    <p-feature-flag
        name="Rental Management" 
        redirect
    >    
        <b-overlay :show="isBusy"
                    rounded
                    opacity="0.5"
                    spinner-small
                    spinner-variant="primary">
        <!-- edit-key="rentalHistoryId" - Edit functionality to be added later.  Add this property to the p-search-screen to show Edit button (which needs to be wired up, too)  -->
            <p-search-screen
                title="Rental Reconciliation"
                ref="searchScreen"
                api-destination="rentals"
                :filters="filters"
                :fields="fields"
                :filterBy="filterBy"
                :use-bootstrap-selection="false"
                :show-set-to="false"
                search-destination="rentalHistory"
                @selected="onSelection"
                @selected-rows-changed="selectedRowsChanged">
                <template v-slot:actionButtons>
                    <b-dropdown text="Export" variant="primary">
                        <b-dropdown-item @click="viewPrintRentalHistoryDetails()">
                            Rental Reconciliation View/Print</b-dropdown-item>
                        <b-dropdown-item @click="exportRentalHistoryDetails()">
                            Rental Reconciliation CSV
                        </b-dropdown-item>
                    </b-dropdown>
                </template>
            
                <template v-slot:belowSearch>
                    <b-row>
                        <b-col cols="12" sm="12" xl="12">
                            <div class="rental-actions-bar">
                                <div>
                                    <p-advanced-select
                                        v-model="filters.termId"
                                        data-type="rentalTerms"
                                        first-load
                                        style="min-width: 180px;"
                                        @selectedObject="termChange($event)"
                                        label="Term" />
                                </div>
                                <div>
                                    <b-button
                                        variant="primary"
                                        :disabled="!selectedRentalRecords.length"
                                        @click="sendRentalReminders">
                                        Send Reminders
                                    </b-button>
                                </div>
                                <div>
                                    <b-button
                                        variant="primary"
                                        :disabled="!selectedRentalRecords.length"
                                        @click="$bvModal.show('changeRentalDueDatesModal')">
                                        Modify Due Dates
                                    </b-button>
                                </div>

                                <div>
                                    <b-button
                                        variant="primary"
                                        :disabled="!selectedRentalRecords.length"
                                        @click="processRentalsNotReturned">
                                        Process RNRs
                                    </b-button>
                                </div>
                                <div>
                                    <b-button
                                        variant="primary"
                                        :disabled="!selectedRentalRecords.length"
                                        @click="processRentalsWriteOffs">
                                        Write Off
                                    </b-button>
                                </div>  
                                <div class="rental-select-instructions">
                                    <div>Check the checkboxes on the left-hand side of the list to select rentals, </div>
                                    <div>then click the "Send Reminders", "Modify Due Dates", or "Process RnRs" button to proceed.</div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                    <div class="mt-3">
                        <p-card>
                            <div class="d-flex justify-content-between text-center">
                                <div class="flex-fill">
                                    <strong>Term</strong><br />
                                    {{ termName }}
                                </div>
                                <div class="flex-fill">
                                    <strong>First Day to Rent</strong><br />
                                    {{ dateFormatter(rentalData.startDate) }}
                                </div>
                                <div class="flex-fill">
                                    <strong>Last Day to Rent</strong><br />
                                    {{ dateFormatter(rentalData.endDate) }}
                                </div>
                                <div class="flex-fill">
                                    <strong>Full Refund Expiration Date</strong><br />
                                    {{ dateFormatter(rentalData.refundExpDate) }}
                                </div>
                                <div class="flex-fill">
                                    <strong>Due Date</strong><br />
                                    {{ dateFormatter(rentalData.dueDate) }}
                                </div>
                            </div>
                        </p-card>
                    </div>
                    <b-row class="mt-4">
                        <b-col>
                        <p-checkbox
                                label="Outstanding (OUT)"
                                class="ms-2"
                                v-model="filters.IncludeOutstanding"
                            />
                        <p-checkbox
                                label="Returned (IN)"
                                class="ms-3"
                                v-model="filters.IncludeReturned"
                            />
                        <p-checkbox
                                label="Rental Not Returned (RNR)"
                                class="ms-3"
                                v-model="filters.IncludeRentalNotReturned"
                            />
                        <p-checkbox
                            label="Refund (REF)"
                            class="ms-3"
                            v-model="filters.IncludeRefunded"
                            />
                        <p-checkbox
                            label="Billed (BIL)"
                            class="ms-3"
                            v-model="filters.IncludeBilled" />
                        <p-checkbox
                            label="Closed (CL)"
                            class="ms-3"
                            v-model="filters.IncludeClosed" />
                        </b-col>
                    </b-row>
                </template>
            </p-search-screen>

                <p-modal
                    size="md"
                    label="Extend Rental Due Date"
                    ref="changeRentalDueDatesModal"
                    name="changeRentalDueDatesModal"
                    :hide-footer="true">
                    <div>
                        <p>
                            This process will alter the due date, and possibly change the rental status for <strong>selected rental(s)</strong> in the Outstanding (OUT) or Rental Not Returned (RNR) statuses.
                        </p>
                        <p-datepicker
                            v-model="rentalDueDate"
                            label="New Due Date"
                        />
                    </div>
                    <div class="d-flex justify-content-end mt-4">
                        <b-button variant="secondary" class="me-2" @click="$bvModal.hide('changeRentalDueDatesModal')">
                            Go Back
                        </b-button>
                        <b-button variant="primary" :disabled="!rentalDueDate" @click="changeRentalDueDates">
                            Proceed
                        </b-button>
                    </div>
                </p-modal>
        </b-overlay>       
    </p-feature-flag>
</template>

<script>
import axios from "axios";
import { dateFormatter, dateOnlyFormatter } from '../../components/Common/Formatters.js';
import SearchStorage from '@/components/mixins/SearchFilterStorage.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';

export default {
    mixins: [SearchStorage],
    data() {
        return {
            isBusy: false,
            filterName: 'RentalReconciliationFilter',
            rentalData: {},
            rentalDueDate: null,
            selectedRentalRecords: [],
            termName: '',
            filters: {
                automatic: null,
                perPage: 100,
                sortBy: 'transactionNumber',
                sortDesc: true,
                termId: null,
                locationId: null,
                IncludeOutstanding: false,
                IncludeReturned: false,
                IncludeRentalNotReturned: false,
                IncludeRefunded: false,
                IncludeBilled: false,
                IncludeClosed: false,
                searchBy: 'All'
            },
            filterBy: [
                'All',
                'First Name',
                'Last Name',
                'Student ID',
                'Author',
                'Title',
                'ISBN',
                'Rental XREF'
            ],
            fields: [
                {
                    label: 'Rental History ID',
                    key: 'rentalHistoryId',
                    hidden: true
                },                
                {
                    label: 'Sale ID',
                    key: 'saleId',
                    hidden: true
                },
                {
                    label: 'Transaction #',
                    key: 'transactionNumber',
                    tdClass: 'text-nowrap'
                },
                {
                    label: 'Last Name',
                    key: 'lastName',
                    sortable: true
                },
                {
                    label: 'First Name',
                    key: 'firstName',
                    sortable: true
                },
                {
                    label: 'Student ID',
                    key: 'studentId',
                    sortable: true
                }, 
                {
                    label: 'Author/Title/ISBN',
                    key: 'authorTitleIsbn',
                    sortable: true
                },               
                {
                    label: 'Author',
                    key: 'author',
                    hidden: true
                },
                {
                    label: 'Title',
                    key: 'title',
                    hidden: true
                },
                {
                    label: 'ISBN',
                    key: 'isbn',
                    hidden: true
                },
                {
                    label: 'Transaction Date',
                    key: 'transactionDate',
                    sortable: true,
                    formatter: dateFormatter
                },
                {
                    label: 'Rental Xref',
                    key: 'rentalXref',
                    tdClass: 'noWrap'
                },
                {
                    label: 'Status',
                    sortable: true,
                    key: 'rentalStatusCode'
                },
                {
                    label: 'Status Detail',
                    sortable: true,
                    key: 'rentalStatus'
                },                
                {
                    label: 'Due Date',
                    key: 'dueDate',
                    sortable: true,
                    formatter: dateFormatter
                },                
                {
                    label: 'Reminder Sent',
                    key: 'reminderSentDate',
                    sortable: true,
                    formatter: dateFormatter
                },
                {
                    label: 'Date Returned',
                    key: 'dateReturned',
                    sortable: true,
                    formatter: dateFormatter
                }
            ]
        };
    },
    methods: {
        dateFormatter: dateFormatter,
        dateOnlyFormatter: dateOnlyFormatter,
        termChange(option) {
            let promise = axios.get('rentals/gettermrental', {
                params: { termId: option.value }
            });

            promise.then(res => {
                this.rentalData = res.data;
            });
            this.termName = option.name;
        },
        onSelection(rentalRecords) {
            this.selectedRentalRecords = rentalRecords;
        },   
        refreshTable() {
            this.$refs?.searchScreen.refreshSearchResults();
        },
        async exportRentalHistoryDetails() {
            if (!this.filters.termId) {
                this.$appWarning('First select a term before exporting the Rental Reconciliation CSV.')
                return;
            }

            this.isBusy = true;
            axios
                .post(
                    '/rentals/rentalHistoryCsv',
                    this.filters,
                    { responseType: 'blob' }
                )
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        `Rental Reconciliation Transactions.csv`,
                        'application/csv'
                    );
                    this.$appSuccess(
                            `Rental Reconciliation report generated successfully.`
                        )
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        async viewPrintRentalHistoryDetails() {
            if (!this.filters.termId) {
                this.$appWarning('First select a term before selecting Rental Reconciliation View/Print.')
                return;
            }

            this.isBusy = true;
            axios.post('/rentals/rentalHistoryViewPrint', this.filters, { responseType: 'text/html' })
            .then(result => {
                var htmlContent = result.data;
                var newTab = window.open();
                newTab.document.write(htmlContent);
                newTab.document.title = 'Invoice Detail';                    
                newTab.document.close();

                this.$appSuccess('Rental Reconciliation report generated successfully.')
            })
            .finally(() => {
                this.isBusy = false;
                this.filters.selectedIds = null;
            });
        },
        sendRentalReminders() {
            this.$bvModal
                .msgBoxConfirm(
                    'This process will send an email to all customers with selected rental records that are in the OUT status. Are you sure you want to proceed?',
                    {
                        title: 'Send Rental Reminders',
                        size: 'md',
                        buttonSize: 'md',
                        okVariant: 'primary',
                        okTitle: 'Proceed',
                        cancelTitle: 'Go Back',
                        footerClass: 'p-2',
                        centered: true
                    })
                .then(value => {
                    if (value) {
                        this.isBusy = true;
                        axios.post('/rentals/sendrentalreminders', this.selectedRentalRecords.map(r => r.rentalHistoryId))
                        .then(response => {
                            const results = response.data;
                            let message = 'Rental reminders sent:\n';
                            if (!results || results.length === 0) {
                                message += 'None';
                            } else {
                                message += results.map(r =>
                                    `Email: ${r.email} ISBN: ${r.isbn} Success: ${r.success ? 'Yes' : 'No'}`
                                ).join('\n');
                            }

                            if (results && results.some(r => !r.success)) {
                                this.$appWarning(message);
                            }
                            else {
                                this.$appSuccess(message);
                            }
                        })
                        .catch(() => {
                            this.$appError('Failed to send rental reminders.');
                        })
                        .finally(() => {
                            this.isBusy = false;
                        });
                    }
                });
        },
        changeRentalDueDates() {
            const rentalHistoryIds = this.selectedRentalRecords.map(record => record.rentalHistoryId);
            this.isBusy = true;
            axios.post('/rentals/changerentalduedates', {
                rentalHistoryIds: rentalHistoryIds,
                rentalDueDate: this.rentalDueDate
            })
            .then(response => {
                const results = response.data;
                let message = 'Rental due dates changed:\n';
                if (!results || results.length === 0) {
                    message += 'None';
                } else {
                    message += results.map(r =>
                        `ISBN: ${r.isbn} Previous Due Date: ${this.dateOnlyFormatter(r.previousDueDate)} New Due Date: ${this.dateOnlyFormatter(r.currentDueDate)}`
                    ).join('\n');
                }

                this.$appSuccess(message);
            })
            .catch(() => {
                this.$appError('Failed to extend rental due dates.');
            })
            .finally(() => {
                this.$bvModal.hide('changeRentalDueDatesModal');
                this.rentalDueDate = null;
                this.selectedRentalRecords = [];
                this.$refs?.searchScreen.clearSelectedRows();
                this.refreshTable();
                this.isBusy = false;
            });
        },
        processRentalsNotReturned() {
            this.$bvModal
                .msgBoxConfirm(
                    "This action will charge customers for any rentals that are beyond their return date (RNR status). Rental(s) and XREFs will not be cleared. Are you sure you wish to proceed?",
                    {
                        title: 'Process Rentals Not Returned',
                        size: 'md',
                        buttonSize: 'md',
                        okVariant: 'primary',
                        okTitle: 'Proceed',
                        cancelTitle: 'Go Back',
                        footerClass: 'p-2',
                        centered: true
                    })
                .then(value => {
                    if (value) {
                        this.isBusy = true;
                        const rentalHistoryIds = this.selectedRentalRecords.map(record => record.rentalHistoryId);
                        axios.post('/rentals/processRentalsNotReturned', {
                            rentalHistoryIds: rentalHistoryIds,
                            locationId: this.filters.locationId
                        })
                        .then(response => {
                            const results = response.data;
                            let message = 'Processed Rentals Not Returned:\n';
                            if (!results || results.length === 0) {
                                message += 'None';
                            } else {
                                message += results.map(r =>
                                    `Success: ${r.success} Email: ${r.posApiResponse.customerEmail} Name: ${r.posApiResponse.customerName} ISBN: ${r.isbn} Amount: ${r.posApiResponse.approvedAmount}`
                                ).join('\n');
                            }

                            if (results && results.some(r => !r.success)) {
                                this.$appWarning(message);
                            } else {
                                this.$appSuccess(message);
                            }
                        })
                        .catch(() => {
                            this.$appError('Failed to process rentals not returned.');
                        })
                        .finally(() => {
                            this.$nextTick(() => {
                                this.$refs.searchScreen.refreshSearchResults();
                                this.isBusy = false;
                            });                            
                        });
                    }
                });
        },
        processRentalsWriteOffs() {
            this.$bvModal
                .msgBoxConfirm(
                    "This action will remove rental(s) from inventory and clear customer liability without charges even if the book is still outstanding. Do you wish to proceed?",
                    {
                        title: 'Process Rentals Write Offs',
                        size: 'md',
                        buttonSize: 'md',
                        okVariant: 'primary',
                        okTitle: 'Proceed',
                        cancelTitle: 'Go Back',
                        footerClass: 'p-2',
                        centered: true
                    })
                .then(value => {
                    if (value) {
                        const rentalHistoryIds = this.selectedRentalRecords.map(record => record.rentalHistoryId);
                        axios.post('/rentals/processRentalsWriteOffs', {
                            rentalHistoryIds: rentalHistoryIds,
                            locationId: this.filters.locationId
                        })
                        .then(response => {
                            const results = response.data;
                            let message = 'Processed Rentals Write Offs:\n';
                            if (!results || results.length === 0) {
                                message += 'None';
                            } else {
                                message += results.map(r =>
                                    `Success: ${r.success} Email: ${r.customerEmail} Name: ${r.customerName} ISBN: ${r.isbn}`
                                ).join('\n');
                            }

                            if (results && results.some(r => !r.success)) {
                                this.$appWarning(message);
                            } else {
                                this.$appSuccess(message);
                            }
                        })
                        .catch(() => {
                            this.$appError('Failed to process rentals write offs.');
                        });
                    }
                });
        },        
        selectedRowsChanged(selectedRentalHistory) {
            this.onSelection(selectedRentalHistory);
        },        
    },
    created: async function() {
        this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
    }
};
</script>
<style scoped>
.rental-actions-bar {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    flex-wrap: wrap;
}

.rental-actions-bar > div:first-child {
    min-width: 180px;
}

.rental-actions-bar :deep(.btn.custom-select),
.rental-actions-bar :deep(.btn.select-button) {
    min-width: 180px;
}

.rental-actions-bar .btn {
    min-width: 160px;
}

.rental-select-instructions {
    margin-top: 11px;
}
</style>